<template>
	<teleport to="body">

		<div id="modal-download-dataset">

			<div class="rp-overlay"></div>

			<div class="rp-modal" style="width: 470px;">

				<div class="rp-modal-header">
					<h3 class="rp-modal-title">{{ $t('Download media') }}</h3>
					<button class="rp-btn-dismiss"
						@click="$emit('closeModal')">
						<XIcon class="w-5 h-5" />
					</button>
				</div>

				<div class="bg-white p-5">

					<div v-if="layout.zip.loading" class="flex flex-col justify-center pb-2">
						
						<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center mt-5 mb-5">
							<LoadingIcon icon="ball-triangle" class="w-12 h-12" />
						</div>

						<div class="text-center pt-5">
							{{ layout.debug }}
						</div>

					</div>

					<div v-else-if="!layout.loading">

						<div class="p-2 text-center">
							<DownloadCloudIcon class="w-10 h-10 text-theme-1 mx-auto" />
							<div class="text-2xl ">{{ $t('Download') }}</div>
							<div class="text-normal">{{ layout.selectedImages.length || layout.images.length }} {{ $t('selected media') }}</div>
						</div>

						<div class="rp-modal-body-buttons gap-3 mt-4">
							<button class="rp-btn-secondary" @click="$emit('closeModal')">
								{{ $t('Cancel') }}
							</button>
							<button class="rp-btn-primary" @click="downloadImages()">
								{{ $t('Download') }}
							</button>
						</div>

					</div>

					<div v-else class="flex-1 flex flex-col justify-center items-center text-center py-5">
						<img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" />
						<LoadingIcon icon="three-dots" class="w-8 h-8 text-blue-500" />
					</div>

				</div>

			</div>

		</div>

	</teleport>
</template>

<script setup>
import { onMounted, onUnmounted, ref } 	from 'vue';
import { useStore }			from "@/store";
import * as rosepetalModel	from "rosepetal-model";
import config 				from '@/etc/rosepetal.json';

const store = useStore();

let $dataset 	= rosepetalModel.default.dataset;
let $firebase	= rosepetalModel.default._firebase;

const $emit = defineEmits(['closeModal']);

const props = defineProps({
    dataset: {
        type: Object,
        required: true,
    },
	selectedImages: {
		type: Array,
		default: () => []
	},
	completeDataset: {
		type: Boolean,
		default: true
	}
});

const layout = ref({
	completeDataset: 	true,
	loading: 			true,
	zip: {
		loading:	false,
		counter: 	0
	},
	dataset: 		{},
	images: 		{},
	selectedImages: [],
	statusMessage: 	''
});

const timeOut = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const loadImages = async () => {
	layout.value.dataset = props.dataset;

	const images = await $dataset.getImages(layout.value.dataset);
	layout.value.images = images.media;
	
	if (props.completeDataset) {
		layout.value.selectedImages = images.media.map(image => image.id);
	}

	layout.value.loading = false;
}

const downloadImages_o = async () => {
	layout.value.zip.loading = true;

    layout.value.debug = 'Preparing ZIP folders...';
	await timeOut(2000);
	layout.value.debug = 'Obtaining images data...';

	const userId = store.state.main.uuid || 'unknown';
	$dataset.downloadZip(layout.value.dataset, layout.value.selectedImages, userId, config.functions.usapi).then(async (resp) => {
		const link = document.createElement('a');
		link.href = resp.data;
		link.setAttribute('download', `${layout.value.dataset.id}.zip`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(resp.data);
	});

	await trackDownload();
}

const downloadImages = async () => {
    layout.value.zip.loading = true;
    layout.value.debug = 'Preparing ZIP folders...';
    await timeOut(2000);
    layout.value.debug = 'Obtaining images data...';

    await trackDownload();

    const userId = store.state.main.uuid || 'unknown';

    const resp = await $dataset.downloadZip(layout.value.dataset, layout.value.selectedImages, userId, config.functions.usapi);
    await finalizeAndDownload(resp);
}

let unsubscribeStatus = null;

const trackDownload = async () => {
    const userId = store.state.main.uuid || 'unknown';
    unsubscribeStatus =  $firebase.trackDocumentFieldChanges('dataset', props.dataset.id, async (newStatus, _) => {
        if (newStatus) {
			if (newStatus.status === 'downloading') {
				if (newStatus.processedImages < newStatus.totalImages) {
					layout.value.debug = `Processing ${newStatus.processedImages} / ${newStatus.totalImages} images...`;
				} else {
					layout.value.debug = `Downloading ZIP...`;
				}
			} else if (newStatus.status === 'finished') {
				await $dataset.clearDownloadTask(props.dataset.id, store.state.main.uuid);
				$emit('closeModal');
			}
		}
    }, `downloadResume.${userId}`);
}

const finalizeAndDownload = async (resp) => {
    const link = document.createElement('a');
    link.href = resp.data;
    link.setAttribute('download', `${layout.value.dataset.id}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(resp.data);
}

onMounted(async () => {
	const userId = store.state.main.uuid || 'unknown';
    layout.value.dataset = await $dataset.get(props.dataset.id);

	const isDownloadInactive = !layout.value.dataset.downloadResume || 
		!layout.value.dataset.downloadResume[userId] ||
		layout.value.dataset.downloadResume[userId].status === 'finished';

	const hasSelectedImages = props.selectedImages && props.selectedImages.length > 0 && !props.completeDataset;

	if (isDownloadInactive) {
		if (hasSelectedImages) {
			layout.value.selectedImages = props.selectedImages;
			layout.value.completeDataset = false;
		}
		await loadImages();
	} else {
		layout.value.zip.loading = true;
		const currentStatus = layout.value.dataset.downloadResume[userId];

		layout.value.processedImages 	= layout.value.dataset.downloadResume[userId].processedImages || 0;
		layout.value.totalImages 		= layout.value.dataset.downloadResume[userId].totalImages || 0;

		if (currentStatus.status === 'setup') {
			layout.value.debug = `Obtaining images data...`;
		} else if (currentStatus.status === 'downloading') {
			layout.value.debug = `Processing ${layout.value.processedImages} / ${layout.value.totalImages} images...`;
		}

		trackDownload();
	}
});

onUnmounted(() => {
	if (unsubscribeStatus) {
		unsubscribeStatus();
		unsubscribeStatus = null;
	}
})
</script>